// src/Subscriber/SubscriberWhatsappChat.jsx
// Read-only WhatsApp chat history for one subscriber (all business numbers).
// GET botsense-chat/report/subscriber/:subscriberId/messages
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { faSync } from "@fortawesome/free-solid-svg-icons";
import "../whatsappChat/styles/inbox.css";
import { waChatClient, normalizeError } from "../whatsappChat/api/client";
import { MessageBubble } from "../whatsappChat/components/MessageBubble";
import { dayKey, formatDay } from "../whatsappChat/api/content";

const PAGE_SIZE = 100;
const POLL_MS = 20000;
const LOAD_OLDER_AT_PX = 40;

// The shared bubble reads `message_at` and a failure string from `error`.
const toBubbleMessage = (m) => ({
  ...m,
  message_at: m.sent_at,
  error: m.failure?.reason || m.error,
});

const mergeById = (rows) => {
  const seen = new Set();
  return rows.filter((r) => (seen.has(r.id) ? false : seen.add(r.id)));
};

const SubscriberWhatsappChat = ({ subscriberId }) => {
  const [canView, setCanView] = useState(false);
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [error, setError] = useState(null);

  const scrollRef = useRef(null);
  // What the next layout pass does with the scroll position.
  const pendingScroll = useRef({ mode: "bottom", prevHeight: 0 });
  const currentId = useRef(subscriberId);
  currentId.current = subscriberId;

  useEffect(() => {
    let cancelled = false;
    waChatClient
      .get("/permissions")
      .then((res) => {
        if (!cancelled)
          setCanView(Boolean(res.data?.data?.subscriber_chat?.view));
      })
      .catch(() => {
        if (!cancelled) setCanView(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchPage = useCallback(
    (params) =>
      waChatClient.get(`/report/subscriber/${subscriberId}/messages`, {
        params: { limit: PAGE_SIZE, ...params },
      }),
    [subscriberId],
  );

  const loadLatest = useCallback(async () => {
    if (!subscriberId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchPage({});
      if (currentId.current !== subscriberId) return;
      pendingScroll.current = { mode: "bottom", prevHeight: 0 };
      setMessages(res.data?.data || []);
      setHasMore(Boolean(res.data?.has_more));
    } catch (err) {
      if (currentId.current !== subscriberId) return;
      setMessages([]);
      setHasMore(false);
      setError(normalizeError(err).message);
    } finally {
      if (currentId.current === subscriberId) setLoading(false);
    }
  }, [subscriberId, fetchPage]);

  useEffect(() => {
    setMessages([]);
    setHasMore(false);
    if (canView) loadLatest();
  }, [canView, loadLatest]);

  // Poll for new messages while the page is visible.
  const lastId = messages.length ? messages[messages.length - 1].id : undefined;
  useEffect(() => {
    if (!canView || error || lastId === undefined) return undefined;
    const timer = setInterval(async () => {
      if (document.hidden) return;
      try {
        const res = await fetchPage({ after_id: lastId });
        const rows = res.data?.data || [];
        if (!rows.length || currentId.current !== subscriberId) return;
        const el = scrollRef.current;
        if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
          pendingScroll.current = { mode: "bottom", prevHeight: 0 };
        }
        setMessages((prev) => mergeById([...prev, ...rows]));
      } catch {
        // Next tick tries again.
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [canView, error, lastId, subscriberId, fetchPage]);

  const loadOlder = async () => {
    if (loadingOlder || !hasMore || !messages.length) return;
    setLoadingOlder(true);
    const el = scrollRef.current;
    try {
      const res = await fetchPage({ before_id: messages[0].id });
      if (currentId.current !== subscriberId) return;
      pendingScroll.current = {
        mode: "keep",
        prevHeight: el ? el.scrollHeight : 0,
      };
      setMessages((prev) => mergeById([...(res.data?.data || []), ...prev]));
      setHasMore(Boolean(res.data?.has_more));
    } catch (err) {
      setError(normalizeError(err).message);
    } finally {
      setLoadingOlder(false);
    }
  };

  const firstId = messages[0]?.id;
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { mode, prevHeight } = pendingScroll.current;
    if (mode === "bottom") el.scrollTop = el.scrollHeight;
    else if (mode === "keep") el.scrollTop += el.scrollHeight - prevHeight;
    pendingScroll.current = { mode: "none", prevHeight: 0 };
  }, [firstId, lastId]);

  if (!canView) return null;

  let body;
  if (loading && !messages.length) {
    body = <div className="sd-wa-state">Loading messages…</div>;
  } else if (error && !messages.length) {
    body = <div className="sd-wa-state text-danger">{error}</div>;
  } else if (!messages.length) {
    body = (
      <div className="sd-wa-state">
        No WhatsApp messages with this subscriber
      </div>
    );
  } else {
    let lastDay = "";
    body = (
      <>
        {hasMore && (
          <div className="wa-thread-older">
            <button
              type="button"
              className="btn btn-default btn-xs"
              onClick={loadOlder}
              disabled={loadingOlder}
            >
              {loadingOlder ? "Loading…" : "Load older messages"}
            </button>
          </div>
        )}
        {messages.map((m) => {
          const day = dayKey(m.sent_at);
          const showDay = day !== lastDay;
          lastDay = day;
          const outbound = m.from === "business";
          const meta = [
            m.number?.label,
            outbound ? m.sent_by_name : null,
          ].filter(Boolean);
          return (
            <React.Fragment key={m.id}>
              {showDay && (
                <div className="wa-day">
                  <span>{formatDay(m.sent_at)}</span>
                </div>
              )}
              <div
                title={
                  m.failure
                    ? [m.failure.code, m.failure.details]
                        .filter(Boolean)
                        .join(" · ")
                    : undefined
                }
              >
                <MessageBubble message={toBubbleMessage(m)} />
                {meta.length > 0 && (
                  <div className={`sd-wa-meta ${outbound ? "is-out" : ""}`}>
                    {meta.join(" · ")}
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </>
    );
  }

  return (
    <div className="sd-wa-fill">
      <div className="panel panel-default with-rounded-corner with-shadow sd-wa-panel">
        <div className="panel-heading border-0 d-flex justify-content-between align-items-center">
          <span className="panel-title mb-0">
            <FontAwesomeIcon icon={faWhatsapp} style={{ color: "#25d366" }} />{" "}
            WhatsApp Chat
          </span>
          <button
            type="button"
            className="btn btn-default btn-xs"
            onClick={loadLatest}
            disabled={loading}
            title="Reload latest messages and statuses"
          >
            <FontAwesomeIcon icon={faSync} spin={loading} />
          </button>
        </div>
        <div className="sd-wa-body">
          <div
            className="sd-wa-scroll"
            ref={scrollRef}
            onScroll={(e) => {
              if (e.currentTarget.scrollTop < LOAD_OLDER_AT_PX) loadOlder();
            }}
          >
            {body}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriberWhatsappChat;
